/**
 * Quote coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const quoteColoringPagesPack: ToilePack = {
    slug: 'quote-coloring-pages',
    category: 'coloring',
    name: 'Quote coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'kind', label: 'Be kind', subject: 'the words "Be Kind" in large outline letters in the center, a wreath of wildflowers and leaves around them'},
        {key: 'dream', label: 'Dream big', subject: 'the words "Dream Big" in bubble letters among stars, a crescent moon and swirling clouds'},
        {key: 'enough', label: 'You are enough', subject: 'the words "You Are Enough" in a ribbon banner across a round mandala of petals and dots'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and open', style: 'thick black outline, wide open letters and large simple decorations, easy to color for children', palette: 'black ink only', framing: 'The lettering and its decoration centered with a wide white margin'},
        {key: 'fine', label: 'Fine and calm', style: 'thin black line, delicate decorations and small repeating details to color slowly', palette: 'black ink only', framing: 'The lettering and its decoration filling the page with a thin white margin'},
        {key: 'patterned', label: 'Patterned', style: 'medium black outline, the letters and decoration filled with small doodle patterns of dots, stripes and leaves', palette: 'black ink only', framing: 'The lettering and its decoration centered with a white margin'},
    ],
    directions: ['Stay curious', 'Love wins', 'Breathe'],
    directionHint: 'try: Stay curious',
    seo: {
        title: 'Quote coloring pages: kind words in outline letters',
        metaDescription: 'Nine quote coloring pages: short sayings in large outline letters, with flowers, stars or a mandala border, in three styles.',
        h1: 'Quote coloring pages: Be kind, Dream big, You are enough',
        tagline: 'A short saying in big outline letters, with a border of flowers, stars or a mandala.',
        intro: 'Quote coloring pages for adults who color to calm down and for teachers who pin a kind word on the classroom wall. The nine pages show a short saying in large outline letters, such as Be kind or Dream big, surrounded by flowers, stars or a mandala border. The styles are bold and open, fine and calm, and patterned. Type a different saying, and the same style carries it.',
        useCases: [
            'a kind word on a classroom wall',
            'a calm page for an adult to color after work',
            'a card or gift with a short message',
            'a morning circle activity for children',
        ],
        faq: [
            {q: 'What are the three sayings?', a: 'Be kind, Dream big and You are enough, each shown in large outline letters.'},
            {q: 'What is the difference between the styles?', a: 'Bold and open uses thick outlines and wide letters, fine and calm uses thin lines and small repeating details, and patterned fills the letters and border with doodled dots, stripes and leaves.'},
            {q: 'Can I ask for a different saying?', a: 'Yes. A saying you type, such as Stay curious, Love wins or Breathe, is a request; the page keeps the same style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and color.'},
        ],
    },
}
